import { jsx as _jsx } from "react/jsx-runtime";
import React, { useState } from 'react';
import { pluralize as plur, capitalize, commaNumber } from '@vitessce/utils';
import { TitleInfo, useReady, useUrls, useGridItemSize, useFeatureLabelsData, useObsFeatureMatrixIndices, useCoordination, useLoaders, useExpandedFeatureLabelsMap, useCoordinationScopes, } from '@vitessce/vit-s';
import { ViewType, COMPONENT_COORDINATION_TYPES, ViewHelpMapping } from '@vitessce/constants-internal';
import { makeStyles } from '@vitessce/styles';
import FeatureList from './FeatureList.js';
import FeatureListOptions from './FeatureListOptions.js';
const useStyles = makeStyles()(theme => ({
    featureListContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.primaryBackground,
        color: theme.palette.primaryForeground,
    },
}));
/**
 * A subscriber component for a gene listing component.
 * @param {object} props
 * @param {string} props.theme The current theme name.
 * @param {object} props.coordinationScopes The mapping from coordination types to coordination
 * scopes.
 * @param {function} props.removeGridComponent The callback function to pass to TitleInfo,
 * to call when the component has been removed from the grid.
 * @param {string} props.title The component title.
 * @param {string} props.variablesLabelOverride The singular form
 * of the name of the variable.
 * @param {string} props.variablesPluralLabelOverride The plural
 * form of the name of the variable.
 * @param {boolean} props.enableMultiSelect If true, allow
 * shift-clicking to select multiple genes.
 * @param {boolean} props.showTable If true, shows a table with the feature name and id.
 * @param {'alphabetical'|'original'} props.sort The sort order of the genes. If sort is defined and
 * it is not equal to `alphabetical`, the genes will be displayed in the feature list in
 * the original order.
 * @param {'featureIndex'|'featureLabels'|null} props.sortKey The information to use for sorting.
 */
export function FeatureListSubscriber(props) {
    const { coordinationScopes: coordinationScopesRaw, removeGridComponent, variablesLabelOverride, theme, title: titleOverride, enableMultiSelect = false, showTable = false, sort = 'alphabetical', sortKey = null, closeButtonVisible, downloadButtonVisible, helpText = ViewHelpMapping.FEATURE_LIST, } = props;
    const loaders = useLoaders();
    const coordinationScopes = useCoordinationScopes(coordinationScopesRaw);
    const [width, height, containerRef] = useGridItemSize();
    const { classes } = useStyles();
    // Get "props" from the coordination space.
    const [{ dataset, obsType, featureType, featureSelection: geneSelection, featureFilter: geneFilter, obsColorEncoding: cellColorEncoding, }, { setFeatureSelection: setGeneSelection, setFeatureFilter: setGeneFilter, setFeatureHighlight: setGeneHighlight, setObsColorEncoding: setCellColorEncoding, }] = useCoordination(COMPONENT_COORDINATION_TYPES[ViewType.FEATURE_LIST], coordinationScopes);
    const variablesLabel = variablesLabelOverride || featureType;
    const title = titleOverride || `${capitalize(variablesLabel)} List`;
    // Get data from loaders using the data hooks.
    // TODO: support multiple feature labels using featureLabelsType coordination values.
    const [{ featureLabelsMap }, featureLabelsStatus, featureLabelsUrls, featureLabelsError,] = useFeatureLabelsData(loaders, dataset, false, {}, {}, { featureType });
    const [expandedFeatureLabelsMap, expandedFeatureLabelsStatus] = useExpandedFeatureLabelsMap(featureType, featureLabelsMap, { stripCuriePrefixes: true });
    const [{ featureIndex }, matrixIndicesStatus, obsFeatureMatrixUrls, matrixIndicesError,] = useObsFeatureMatrixIndices(loaders, dataset, true, { obsType, featureType });
    // Consolidate error values from data hooks.
    const errors = [
        featureLabelsError,
        matrixIndicesError,
    ];
    const isReady = useReady([
        featureLabelsStatus,
        expandedFeatureLabelsStatus,
        matrixIndicesStatus,
    ]);
    const urls = useUrls([
        featureLabelsUrls,
        obsFeatureMatrixUrls,
    ]);
    const geneList = featureIndex || [];
    const numGenes = geneList.length;
    const hasFeatureLabels = Boolean(expandedFeatureLabelsMap);
    function setGeneSelectionAndColorEncoding(newSelection) {
        setGeneSelection(newSelection);
        setCellColorEncoding('geneSelection');
    }
    const [showFeatureTable, setShowFeatureTable] = useState(showTable);
    const [featureListSort, setFeatureListSort] = useState(sort);
    const [featureListSortKey, setFeatureListSortKey] = useState(null);
    const initialSortKey = sortKey || (hasFeatureLabels ? 'featureLabels' : 'featureIndex');
    const primaryColumnName = `${capitalize(featureType)} ID`;
    return (_jsx(TitleInfo, { title: title, info: `${commaNumber(numGenes)} ${plur(variablesLabel, numGenes)}`, theme: theme, withPadding: false, closeButtonVisible: closeButtonVisible, downloadButtonVisible: downloadButtonVisible, removeGridComponent: removeGridComponent, isReady: isReady, urls: urls, helpText: helpText, errors: errors, options: (_jsx(FeatureListOptions, { featureListSort: featureListSort, setFeatureListSort: setFeatureListSort, featureListSortKey: featureListSortKey || initialSortKey, setFeatureListSortKey: setFeatureListSortKey, showFeatureTable: showFeatureTable, setShowFeatureTable: setShowFeatureTable, hasFeatureLabels: hasFeatureLabels, primaryColumnName: primaryColumnName })), children: _jsx("div", { ref: containerRef, className: classes.featureListContainer, children: _jsx(FeatureList, { width: width, height: height, hasColorEncoding: cellColorEncoding === 'geneSelection', showFeatureTable: showFeatureTable, geneList: geneList, featureListSort: featureListSort, featureListSortKey: featureListSortKey || initialSortKey, featureLabelsMap: expandedFeatureLabelsMap, featureType: featureType, geneSelection: geneSelection, geneFilter: geneFilter, setGeneSelection: setGeneSelectionAndColorEncoding, setGeneFilter: setGeneFilter, setGeneHighlight: setGeneHighlight, enableMultiSelect: enableMultiSelect, hasFeatureLabels: hasFeatureLabels, primaryColumnName: primaryColumnName }) }) }));
}
//# sourceMappingURL=FeatureListSubscriber.js.map